import React from 'react';
import { Input, Select, ColorPicker } from 'antd';
import type { Color } from 'antd/es/color-picker';
import type { MediaFile } from './types';
import { filterMediaFilesByType } from './utils';

const { Option } = Select;

interface BackgroundValueInputProps {
  type: 'color' | 'image' | 'gif' | 'video';
  value?: string;
  onChange?: (value: string) => void;
  mediaFiles: MediaFile[];
  loading?: boolean;
  placeholder?: string;
  companyId?: number;
  companyCode?: string;
}

const BackgroundValueInput: React.FC<BackgroundValueInputProps> = ({
  type,
  value,
  onChange,
  mediaFiles,
  loading = false,
  placeholder = '배경 값 입력',
  companyId,
  companyCode
}) => {
  const handleColorChange = (color: Color) => {
    onChange?.(color.toHexString());
  };

  const handleSelectChange = (selectedValue: string) => {
    // 비어있거나 빈 문자열인 경우 그대로 전달
    if (!selectedValue || selectedValue === '') {
      onChange?.(selectedValue);
      return;
    }

    // 선택된 미디어 파일 찾기
    const selectedFile = mediaFiles.find(f => f.filename === selectedValue);

    if (selectedFile && selectedFile.file_path) {
      // file_path에서 'media/' 제거하여 올바른 경로 생성
      // 예: media/company_0/xxx.mp4 → company_0/xxx.mp4
      const fullPath = selectedFile.file_path.replace(/^media\//, '');
      onChange?.(fullPath);
      console.log(`📁 배경 미디어 경로 생성 (file_path 기반): ${selectedValue} → ${fullPath}`);
      console.log(`   - media.company_id: ${selectedFile.company_id}`);
      console.log(`   - media.file_path: ${selectedFile.file_path}`);
    } else {
      // file_path가 없는 경우 파일명만 사용 (Fallback)
      onChange?.(selectedValue);
      console.log(`⚠️ file_path 없음, 파일명만 사용: ${selectedValue}`);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  if (type === 'color') {
    return (
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <ColorPicker
          value={value || '#ffffff'}
          onChange={handleColorChange}
          showText
          format="hex"
          style={{ flex: 1 }}
        />
        <Input
          value={value}
          onChange={handleInputChange}
          placeholder="#ffffff"
          maxLength={7}
          style={{ width: 120 }}
        />
      </div>
    );
  }

  if (type === 'image' || type === 'gif' || type === 'video') {
    const filteredFiles = filterMediaFilesByType(mediaFiles, type);

    return (
      <Select
        value={value}
        onChange={handleSelectChange}
        placeholder={`${type === 'image' ? '이미지' : type === 'gif' ? 'GIF' : '비디오'} 파일 선택`}
        loading={loading}
        allowClear
        showSearch
        filterOption={(input, option) =>
          (option?.children as string)?.toLowerCase().includes(input.toLowerCase())
        }
        style={{ width: '100%' }}
      >
        {filteredFiles.map(file => {
          // 현재 선택된 값이 company 경로를 포함하는지 확인
          const fullPath = companyId !== undefined && companyId !== null ? `company_${companyId}/${file.filename}` : file.filename;
          const isSelected = value === fullPath || value === file.filename;

          return (
            <Option key={file.id} value={file.filename}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <span>{file.title || file.filename}</span>
                  {companyId !== undefined && companyId !== null && (
                    <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>
                      저장 경로: company_{companyId}/{file.filename}
                      {companyId === 0 && <span style={{ color: '#52c41a', fontWeight: 'bold' }}> (시스템 공용)</span>}
                    </div>
                  )}
                </div>
                <span style={{ color: '#666', fontSize: '12px' }}>
                  ({(file.file_size / 1024 / 1024).toFixed(1)}MB)
                </span>
              </div>
            </Option>
          );
        })}
      </Select>
    );
  }

  // 기본 Input
  return (
    <Input
      value={value}
      onChange={handleInputChange}
      placeholder={placeholder}
      style={{ width: '100%' }}
    />
  );
};

export default BackgroundValueInput;